import { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';

import { useMediaQuery } from '@react-hookz/web';
import classNames from 'classnames';
import clamp from 'lodash/clamp';
import throttle from 'lodash/throttle';
import { Sticky, StickyContainer } from 'react-sticky';

import { UNIFIED } from 'src/app/analytics/analyticsEvents';
import Button from 'src/app/components/Elements/Button';
import { HeaderLogoLink } from 'src/app/components/Elements/HeaderLogoLink';
import { Typography } from 'src/app/components/Elements/Typography';
import { NavTheme } from 'src/app/components/Header/constants';
import { LeftNav, NavWrapper, RightNav } from 'src/app/components/Header/styles';
import { ELEMENT_IDS as PROMOTIONAL_OTC_ELEMENT_IDS } from 'src/app/components/Pages/PromotionalLandingPage/constants';
import { LayoutRoutes } from 'src/app/popsicle/router/layoutRoutes';
import { smoothFocus } from 'src/app/utils/smoothFocus';
import { NAV_HEIGHT } from 'src/consts';
import { theme } from 'src/styles/theme';

import type { PromotionalPropsFromRedux } from './container';
import { LogoDivider } from './styles';
import styles from './styles.module.scss';

const displayName = 'Promotional';

interface PromotionalProps extends PromotionalPropsFromRedux {}

export const PromotionalRenderer = ({
  dispatchSegmentTrack,
  toggleShowFAQModal,
  showFaqModal,
  promoImageDetails,
  isSimplePromoLayout,
  hasFaqModal,
  isGetStartedCTA,
  onRedirectToOnboarding,
  promoCode,
}: PromotionalProps) => {
  const [navBarTheme, setNavBarTheme] = useState(NavTheme.blue);
  const [navOpacity, setNavOpacity] = useState(1);
  const [stickyTopOffset, setStickyTopOffset] = useState(0);

  const isMobile = useMediaQuery(theme.breakpoints.smallDown);
  const handleToggleFaqModal = () => {
    toggleShowFAQModal(!showFaqModal);
  };

  useEffect(() => {
    const getScrollDepth = throttle(() => {
      const stickyanchor = document.querySelector('#stickyanchor');
      if (!stickyanchor) {
        return;
      }

      if (isMobile) {
        // Removes sticky nav by making sticky offset larger than window height
        // for sticky cta on mobile
        const windowHeight = document.documentElement.scrollHeight || document.body.scrollHeight;
        setStickyTopOffset(windowHeight + 200);
        setNavBarTheme(NavTheme.blue);
        setNavOpacity(1);
        return;
      }
      setStickyTopOffset((stickyanchor as any).offsetTop);
      const scroll = window.scrollY || window.pageYOffset;
      if (scroll <= NAV_HEIGHT + 100) {
        setNavBarTheme(NavTheme.blue);
        setNavOpacity(1);
      } else {
        const navOpacity = clamp(0.5 + (scroll - (stickyanchor as any).offsetTop) * 0.01, 0, 1);
        setNavBarTheme(NavTheme.white);
        setNavOpacity(navOpacity);
      }
    }, 100);

    getScrollDepth();
    window.addEventListener('scroll', getScrollDepth);

    return () => {
      window.removeEventListener('scroll', getScrollDepth);
    };
  }, [setStickyTopOffset, setNavBarTheme, setNavOpacity, isMobile, promoImageDetails]);

  const handleGetStartedClick = () => {
    dispatchSegmentTrack(UNIFIED.selectOTCSignup, {});
    if (isGetStartedCTA) {
      onRedirectToOnboarding(promoCode);
    } else {
      smoothFocus(PROMOTIONAL_OTC_ELEMENT_IDS.OTC, 0);
    }
  };

  if (isSimplePromoLayout) {
    return (
      <div className={classNames(styles.component, 'marketing', 'layout-marketing')}>
        <div className={classNames(styles.content)}>
          <NavWrapper navTheme={NavTheme.blue}>
            <LeftNav>
              <HeaderLogoLink />
              {promoImageDetails && (
                <>
                  <LogoDivider />
                  <img
                    src={promoImageDetails.logoPath}
                    alt={promoImageDetails.logoAlt}
                    height={promoImageDetails.logoHeight}
                  />
                </>
              )}
            </LeftNav>
            {hasFaqModal && (
              <RightNav noFlex>
                <Button style={'inlineLink'} size={'small'} onClick={handleToggleFaqModal}>
                  <Typography variant={'body'} marginBottom={'none'} color={'white'}>
                    Questions
                  </Typography>
                </Button>
              </RightNav>
            )}
          </NavWrapper>
          <LayoutRoutes renderKey="content" displayName={displayName} location={location} />
        </div>
      </div>
    );
  }

  const isNavThemeWhite = navBarTheme === NavTheme.white;
  const isPromoLogoHidden = promoImageDetails?.isHiddenOnStickyHeader && isNavThemeWhite;

  return (
    <StickyContainer>
      <div className={classNames(styles.component, 'marketing', 'layout-marketing')}>
        <div className={classNames(styles.content)}>
          <Sticky topOffset={stickyTopOffset}>
            {({ style }: { style: CSSProperties }) => {
              return (
                <NavWrapper style={{ opacity: navOpacity, ...style }} navTheme={navBarTheme}>
                  <LeftNav>
                    <HeaderLogoLink />
                    {promoImageDetails && !isPromoLogoHidden && (
                      <>
                        <LogoDivider />
                        <img
                          src={promoImageDetails.logoPath}
                          alt={promoImageDetails.logoAlt}
                          height={promoImageDetails.logoHeight}
                        />
                      </>
                    )}
                  </LeftNav>
                  <RightNav noFlex>
                    {isNavThemeWhite && (
                      <Button size="medium" style="secondary" fixedWidth onClick={handleGetStartedClick}>
                        Get Started
                      </Button>
                    )}
                  </RightNav>
                </NavWrapper>
              );
            }}
          </Sticky>
          <LayoutRoutes renderKey="content" displayName={displayName} location={location} />
        </div>
      </div>
    </StickyContainer>
  );
};

PromotionalRenderer.displayName = displayName;
